/**
 * <console-headline> — the event console's identity band: which event this is,
 * where it stands in its own lifecycle, and the ONE figure that matters in
 * that life. The programme block and the traffic report stand beneath it, in
 * the destination's own scroller.
 *
 * AN EVENT HAS THREE LIVES AND THE BAND SAYS WHAT MATTERS IN EACH. On sale the
 * lead is tickets issued; while the doors are open it is checked-in-of-valid;
 * afterwards it is the final take. One lead figure at display scale per phase;
 * every other figure is a step under it and labelled. A band that led with
 * three figures at once led with none.
 *
 * TICKETS ISSUED IS A SCALAR AND A DOOR. It opens the disjoint origin
 * partition: Sold and Issued to guests. Capacity stays in the view model for
 * the workflows that need it but no graph or denominator is rendered here.
 *
 * ONE FRACTION ON THE BAND, AT MOST. Checked-in-over-valid appears only while
 * attendance is the lead. Ticket issuance is always a scalar. Where it is not
 * the lead it demotes to the placing line ("240 sold" / "Sold out").
 *
 * PACE IS A DIRECTION WORD, NEVER A NUMBER. last7d against prev7d answers "are
 * we on pace", but a seven-day FIGURE printed above a report with its own date
 * picker gets read as scoped by that picker, and an arrow implies a magnitude
 * the band cannot honestly scale. Past ±15% the band says which way; inside
 * the threshold it says nothing, because "same as last week" spends a line to
 * say nothing. On sale only.
 *
 * WHAT THE BAND DOES NOT CARRY, and why each absence is deliberate:
 *
 * · No poster. The art answers no question an operator opens this console to
 *   ask, while it was the band's loudest object and the one thing that set
 *   the band's height; the event's name identifies it, and the full-size art
 *   lives on the event's own record.
 * · No VAT-and-fees caption under the money. It previews the overlay the `›`
 *   already opens. The LABEL carries the honesty instead — while fees are
 *   unreported the figure is headed "Gross taken", never "You keep", and while
 *   they are still landing the label adds `at most` on a line of its own — so a
 *   reader who never presses is never misled, and an upper bound is said as a
 *   fact ABOUT the figure rather than folded into it.
 * · No approved-but-not-charged line and no refunded/cancelled-on-paid
 *   count. Those ride the Dashboard destination's own exceptions row
 *   (dashboard.view.ts), not the identity band.
 *
 * MONEY IS THE BAND'S OTHER DETAIL. Its tint, focus ring, and chevron separate
 * that breakdown from the issued-ticket breakdown. The amount is the plain
 * currency figure and never a sentence:
 * whatever is uncertain about it is said in the label, so the number stays a
 * number. It leads at display scale only once the event is over, when the take
 * IS the answer; while the shop or the door is live it is a support figure.
 *
 * NO MOTION. The live dot is a static filled dot with a soft halo, not a
 * pulse: a header is on screen all session, and motion on it reads as noise by
 * the fortieth view.
 *
 * HEIGHT PARITY. The display line is the tallest object in every phase and in
 * the skeleton, so the band's height is fixed structurally.
 *
 * THE ACCESS GATE. Everything sales-derived stays behind the sales-report
 * grant: tickets issued, its breakdown, the money, the pace, the shop's
 * presence. On sale without the grant the band has no lead figure at all — the
 * lead cell becomes the lock and the one sentence naming what is missing, and
 * the band still holds its box. Once the doors have opened a door count is not
 * a sale, so checked-in still leads and the lock removes only the money and
 * the pace.
 *
 * THE NAV PUCK is fixed 56×56 at (0,0) over every admin destination. The inset
 * for it rides the NAME LINE alone (`pl-14 md:pl-0`), never the band or the
 * surface: insetting the whole band would spend 56px on the one screen where
 * the fraction is the only thing that matters.
 *
 * THE VENUE IS PRINTED ONLY WHERE THERE IS MORE THAN ONE. A single-venue shop
 * would otherwise print its own name on every event header it ever draws.
 *
 * NOTHING IS STATED BEFORE IT CAN BE READ. Until the event's own record lands
 * the band holds its box with skeletons rather than painting nothing and then
 * shoving the report down.
 *
 * Zero properties: it resolves its own subject from `consoleEventId` through
 * the same view model every other console destination reads.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import "@mhmo91/schmancy/skeleton";
import "@mhmo91/schmancy/visually-hidden";
import { formatCurrency, formatDateRange, formatNumber } from "@mhmo91/rx";
import { html, type TemplateResult } from "lit";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { canViewSaleReports } from "../../admin/sale/sale.state.ts";
import { consolePresence } from "../../analytics/presence.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { locationsState } from "../../storefront/locations.state.ts";
import { resolveVenueLabel, resolveVenueTimezone } from "../events.state.ts";
import "./revenue-breakdown.view.ts";
import {
	CURRENCY,
	consoleDoorFigures,
	consoleEvent,
	consoleEventId,
	consoleNotLive,
	consoleOrdersStatus,
	consoleSaleFigures,
	consoleTicketsStatus,
	consoleViewModel,
} from "./console.state.ts";

/** A support cell: a figure one rung under the lead, its words beside it on
 *  its own baseline, and — where the reading moves while an operator watches —
 *  the static dot that says so. The second line is the qualifier ABOUT the
 *  figure ("at most", the pace word), never a second figure. */
function supportCell(
	figure: string,
	label: string,
	second: string | null,
	live: boolean,
): TemplateResult {
	return html`
		<div class="flex min-w-0 flex-wrap items-end gap-x-2.5 gap-y-0.5">
			<schmancy-typography type="headline" token="sm" class="shrink-0 whitespace-nowrap tabular-nums">${figure}</schmancy-typography>
			<div class="flex min-w-0 flex-col gap-0.5">
				<span class="flex items-center gap-1.5">
					${when(
						live,
						() =>
							html`<span class="size-1.5 shrink-0 rounded-full bg-primary-default ring-4 ring-primary-default/15"></span>`,
					)}
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${label}</schmancy-typography>
				</span>
				${when(
					second !== null,
					() =>
						html`<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${second}</schmancy-typography>`,
				)}
			</div>
		</div>
	`;
}

@schmancyElement("console-headline")
export class ConsoleHeadline extends LocalizeMixin(SchmancyElement) {
	override render(): TemplateResult {
		const eventId = consoleEventId.value;
		if (eventId === null) return html``;
		const event = consoleEvent.get();
		// The event's own record is the last thing to land — its catalog is
		// answered by a reconciler of its own — so the band holds its shape and
		// its box rather than painting nothing and then shoving the report down.
		if (event === null) {
			return html`
				<schmancy-surface
					type="container"
					opacity="translucent"
					class="flex flex-col overflow-hidden rounded-bl-2xl rounded-br-bezel px-4 py-3 md:px-6 md:py-4"
				>
					<schmancy-visually-hidden role="status" aria-live="polite">
						${this.t("admin.events.console.loadingFigures")}
					</schmancy-visually-hidden>
					<div class="flex flex-wrap items-end gap-x-8 gap-y-3">
						<div class="flex min-w-48 max-w-xl flex-1 flex-col gap-1" aria-hidden="true">
							<schmancy-skeleton shape="text" class="h-7 w-64"></schmancy-skeleton>
							<schmancy-skeleton shape="text" class="h-4 w-56"></schmancy-skeleton>
						</div>
						<div class="flex flex-wrap items-end gap-x-8 gap-y-3" aria-hidden="true">
							<!-- The on-sale shape, because the phase is unknowable before
							     the record lands: the lead's display line box, the rule
							     beneath it, and two support cells a rung under. h-11 is
							     display sm's line box at its 600px ceiling; the ramp bottoms
							     out at 32px, so on a phone this over-reserves and the band
							     settles UP rather than jumping down. -->
							<div class="flex w-full flex-col gap-2 sm:w-auto sm:min-w-56">
								<schmancy-skeleton shape="text" class="h-11 w-40"></schmancy-skeleton>
								<schmancy-skeleton shape="text" class="h-2 w-full"></schmancy-skeleton>
							</div>
							<div class="flex items-end gap-2.5">
								<schmancy-skeleton shape="text" class="h-8 w-28"></schmancy-skeleton>
								<schmancy-skeleton shape="text" class="mb-1 h-4 w-20"></schmancy-skeleton>
							</div>
							<div class="flex items-end gap-2.5">
								<schmancy-skeleton shape="text" class="h-8 w-10"></schmancy-skeleton>
								<schmancy-skeleton shape="text" class="mb-1 h-4 w-24"></schmancy-skeleton>
							</div>
						</div>
					</div>
				</schmancy-surface>
			`;
		}
		const vm = consoleViewModel.get();
		// Sale figures wait for the orders read, door figures for the tickets
		// read; until one answers its cells hold their box with a skeleton, and
		// a read that failed says so in their place — never a zero.
		const sale = consoleSaleFigures.get();
		const door = consoleDoorFigures.get();
		const granted = canViewSaleReports.get();
		const figuresPending =
			(granted && consoleOrdersStatus.value === "loading") ||
			consoleTicketsStatus.value === "loading";
		const ordersFailed = html`<schmancy-typography type="body" token="sm" class="max-w-xs text-error-default">${this.t("admin.events.console.ordersLoadFailed")}</schmancy-typography>`;
		const lifecycle = vm?.lifecycle ?? "onSale";
		const tz = resolveVenueTimezone(event.locationId, locationsState.value);
		const venueLabel =
			locationsState.value.length > 1
				? resolveVenueLabel(event.locationId, locationsState.value)
				: null;
		const schedule = formatDateRange(
			event.schedule.start.toDate(),
			event.schedule.end.toDate(),
			{ month: "short", timeZone: tz },
		);
		// Sell-through demoted: a scalar on the placing line wherever the band's
		// one fraction is the door's, so the count is still stated and cannot be
		// read as the fraction beside it.
		const soldScalar =
			sale === null ||
			!granted ||
			lifecycle === "onSale" ||
			sale.totalCapacity <= 0
				? null
				: sale.totalSold >= sale.totalCapacity
					? this.t("admin.events.console.soldOutLabel")
					: this.t("admin.events.console.soldScalar", {
							count: formatNumber(sale.totalSold, this.locale),
						});
		// Two seven-day counts, one direction word, no figure. Inside ±15% the
		// line is absent; from a standing start any sale at all is faster.
		const pace =
			sale === null || lifecycle !== "onSale" || !granted
				? null
				: sale.prev7d === 0
					? sale.last7d > 0
						? this.t("admin.events.console.paceUp")
						: null
					: sale.last7d > sale.prev7d * 1.15
						? this.t("admin.events.console.paceUp")
						: sale.last7d < sale.prev7d * 0.85
							? this.t("admin.events.console.paceDown")
							: null;
		// The fee-report ladder the view model's own doc comments define. While no
		// PSP order has a fee report the amount is the gross, headed by its own
		// honest label; with every report in (or no PSP leg at all) it is the exact
		// you-keep amount; in between it is that same amount as an upper bound,
		// because fees only grow as reports land. Every rung of the ladder is said
		// in the label — the figure is the amount and nothing else.
		const feesPending = sale === null || sale.youKeepCents === null;
		const feesPartial =
			!feesPending &&
			sale !== null &&
			sale.feeOrdersTotal > 0 &&
			sale.feeOrdersReported < sale.feeOrdersTotal;
		const moneyFigure = formatCurrency(
			((feesPending ? sale?.grossTakenCents : sale?.youKeepCents) ?? 0) / 100,
			CURRENCY,
			this.locale,
		);
		const moneyLabel = this.t(
			feesPending
				? "admin.events.console.grossTakenLabel"
				: "admin.events.console.youKeepLabel",
		);
		// There is no money question before there are ticket types, and the cell
		// opens a breakdown only once there is a paid order to break down.
		const moneyShown = granted && vm?.hasTicketTypes === true;
		const moneyLead = lifecycle === "after";
		const moneyOpens = sale?.orders.some((o) => o.status === "paid") === true;
		// One cell, drawn once, placed by the phase: the lead slot once the event
		// is over, the support row while the shop or the door is live. Only its
		// type scale moves between the two — a second copy of the band's one door
		// would be a second door.
		const money = !moneyShown
			? null
			: sale === null
				? consoleOrdersStatus.value === "error"
					? lifecycle === "onSale"
						? null
						: ordersFailed
					: html`
					<div class="flex items-end gap-2.5" aria-hidden="true">
						<schmancy-skeleton shape="text" class=${moneyLead ? "h-11 w-40" : "h-8 w-28"}></schmancy-skeleton>
						<schmancy-skeleton shape="text" class="mb-1 h-4 w-20"></schmancy-skeleton>
					</div>
				`
				: html`
				<div class="flex min-w-0 flex-wrap items-end gap-x-2.5 gap-y-0.5">
					<schmancy-typography type=${moneyLead ? "display" : "headline"} token="sm" weight="medium" class="shrink-0 whitespace-nowrap tabular-nums">${moneyFigure}</schmancy-typography>
					<div class="flex min-w-0 flex-col gap-0.5 pb-1">
						<span class="flex items-center gap-1">
							<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${moneyLabel}</schmancy-typography>
							${when(
								moneyOpens,
								() =>
									html`<schmancy-icon class="shrink-0 text-base text-surface-onVariant" aria-hidden="true">chevron_right</schmancy-icon>`,
							)}
						</span>
						${when(
							feesPartial,
							() =>
								html`<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.atMost")}</schmancy-typography>`,
						)}
					</div>
				</div>
			`;
		const moneyCell =
			money === null
				? null
				: sale === null || !moneyOpens
					? money
					: html`
						<!-- The tint bleeds past the text on every side; -m-2 cancels p-2
						     exactly, so the cell keeps the box its sibling aligns to. -->
						<button
							type="button"
							class="-m-2 flex min-w-0 cursor-pointer touch-manipulation items-end gap-2.5 rounded-lg p-2 text-left transition-colors duration-150 hover:bg-surface-container active:bg-surface-containerHigh focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default"
							@click=${() =>
								show(() => html`<revenue-breakdown></revenue-breakdown>`, {
									ariaLabel: this.t(
										"admin.events.console.revenueBreakdown.dialogAriaLabel",
									),
								})
									.pipe(takeUntil(this.disconnecting))
									.subscribe()}
						>
							${money}
							<!-- The name is computed from the cell's own contents, so the
							     figure a reader came for is the first thing said and the
							     verb follows it. -->
							<schmancy-visually-hidden>
								${this.t(
									feesPending
										? "admin.events.console.grossTakenBreakdownAria"
										: "admin.events.console.youKeepBreakdownAria",
								)}
							</schmancy-visually-hidden>
						</button>
					`;
		// The lock is a whole cell wherever the money would have stood: a session
		// told it may not read sale figures is told so, rather than left to infer
		// it from a slot that quietly went missing.
		const lockCell = html`
			<div class="flex w-full flex-col gap-1.5 sm:w-auto sm:min-w-56">
				<schmancy-icon class="text-xl text-surface-onVariant">lock</schmancy-icon>
				<schmancy-typography type="body" token="sm" class="max-w-xs text-surface-onVariant">
					${this.t("admin.events.console.noAccessBody")}
				</schmancy-typography>
			</div>
		`;
		const attendanceKnown = door?.attendanceKnown === true;
		const checkedInFigure =
			door === null
				? ""
				: this.t("admin.events.console.checkedInFraction", {
						redeemed: door.redeemedTickets,
						valid: door.validTickets,
					});
		// A session that may not read tickets gets no door cell at all.
		const doorUnanswered =
			consoleTicketsStatus.value === "loading"
				? html`
					<div class="flex items-end gap-2.5" aria-hidden="true">
						<schmancy-skeleton shape="text" class="h-11 w-40"></schmancy-skeleton>
						<schmancy-skeleton shape="text" class="mb-1 h-4 w-20"></schmancy-skeleton>
					</div>
				`
				: consoleTicketsStatus.value === "error"
					? html`<schmancy-typography type="body" token="sm" class="max-w-xs text-error-default">${this.t("admin.events.console.ticketsLoadFailed")}</schmancy-typography>`
					: null;
		const issuedTicketsCell =
			sale === null
				? consoleOrdersStatus.value === "error"
					? ordersFailed
					: html`
					<div class="flex items-end gap-2.5" aria-hidden="true">
						<schmancy-skeleton shape="text" class="h-11 w-24"></schmancy-skeleton>
						<schmancy-skeleton shape="text" class="mb-1 h-4 w-24"></schmancy-skeleton>
					</div>
				`
				: html`
			<button
				type="button"
				class="-m-2 flex min-w-0 cursor-pointer touch-manipulation flex-wrap items-end gap-x-2.5 gap-y-0.5 rounded-lg p-2 text-left transition-colors duration-150 hover:bg-surface-container active:bg-surface-containerHigh focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default"
				@click=${() =>
					show(
						() => html`
							<schmancy-surface type="container" class="grid min-w-72 gap-4 p-6">
								<schmancy-typography type="headline" token="sm">
									${this.t("admin.events.console.ticketsIssuedLabel")}
								</schmancy-typography>
								<div class="grid gap-3">
									<div class="flex items-baseline justify-between gap-8">
										<schmancy-typography type="body" token="md">${this.t("admin.events.console.soldLabel")}</schmancy-typography>
										<schmancy-typography type="headline" token="sm" class="tabular-nums">${formatNumber(sale.soldTickets, this.locale)}</schmancy-typography>
									</div>
									<div class="flex items-baseline justify-between gap-8">
										<schmancy-typography type="body" token="md">${this.t("admin.events.console.issuedToGuestsLabel")}</schmancy-typography>
										<schmancy-typography type="headline" token="sm" class="tabular-nums">${formatNumber(sale.guestTickets, this.locale)}</schmancy-typography>
									</div>
								</div>
							</schmancy-surface>
						`,
						{
							ariaLabel: this.t(
								"admin.events.console.ticketsIssuedBreakdownDialogAria",
							),
						},
					)
						.pipe(takeUntil(this.disconnecting))
						.subscribe()}
			>
				<schmancy-typography type="display" token="sm" weight="medium" class="shrink-0 whitespace-nowrap tabular-nums text-primary-default">
					${formatNumber(sale.totalSold, this.locale)}
				</schmancy-typography>
				<schmancy-typography type="label" token="sm" class="shrink-0 whitespace-nowrap pb-1 text-surface-onVariant">
					${this.t("admin.events.console.ticketsIssuedLabel")}
				</schmancy-typography>
				<schmancy-visually-hidden>
					${this.t("admin.events.console.ticketsIssuedBreakdownAria")}
				</schmancy-visually-hidden>
			</button>
		`;
		// The phase elects the lead, and the grant can take it away only while
		// the band's lead is a sale figure. Every branch is one display-scale
		// figure over the rule's own height, so the band never changes height.
		const leadCell =
			lifecycle === "onSale"
				? !granted
					? lockCell
					: issuedTicketsCell
				: lifecycle === "present"
					? html`
						<div class="flex w-full flex-col gap-2 sm:w-auto sm:min-w-56">
							${
								door === null
									? html`${doorUnanswered}<div class="h-2" aria-hidden="true"></div>`
									: attendanceKnown
										? html`
									<div class="flex min-w-0 flex-wrap items-end gap-x-2.5 gap-y-0.5">
										<schmancy-typography type="display" token="sm" weight="medium" class="shrink-0 whitespace-nowrap tabular-nums text-primary-default">${checkedInFigure}</schmancy-typography>
										<schmancy-typography type="label" token="sm" class="shrink-0 whitespace-nowrap pb-1 text-surface-onVariant">${this.t("admin.events.console.presentLabel")}</schmancy-typography>
									</div>
									<!-- The same room filling a second time — seats sold, then
									     seats arrived. The fraction above already states the
									     count, so the rule is ink and says nothing again. -->
									<schmancy-progress
										size="lg"
										aria-hidden="true"
										value=${door.redeemedTickets}
										max=${door.validTickets}
									></schmancy-progress>
								`
										: html`
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.noCheckInsYet")}</schmancy-typography>
									<div class="h-2" aria-hidden="true"></div>
								`
							}
						</div>
					`
					: html`
						<div class="flex w-full flex-col gap-2 sm:w-auto sm:min-w-56">
							${
								moneyCell ??
								(door === null
									? doorUnanswered
									: attendanceKnown
										? html`
										<div class="flex min-w-0 flex-wrap items-end gap-x-2.5 gap-y-0.5">
											<schmancy-typography type="display" token="sm" weight="medium" class="shrink-0 whitespace-nowrap tabular-nums text-primary-default">${checkedInFigure}</schmancy-typography>
											<schmancy-typography type="label" token="sm" class="shrink-0 whitespace-nowrap pb-1 text-surface-onVariant">${this.t("admin.events.console.attendanceFinal")}</schmancy-typography>
										</div>
									`
										: html`<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.noCheckInsYet")}</schmancy-typography>`)
							}
							<!-- Nothing is filling any more, so there is no rule to draw —
							     but its height is still reserved, or the band would settle
							     up the moment the doors closed. -->
							<div class="h-2" aria-hidden="true"></div>
						</div>
					`;
		// Everything a step under the lead. The shop's presence is a sale figure
		// and rides the same grant as the money beside it; a door count is not,
		// and stands whatever the grant says.
		const presence = consolePresence.get();
		const supportCells: readonly (TemplateResult | null)[] =
			lifecycle === "onSale"
				? !granted
					? []
					: [
							moneyCell,
							// No poll has answered under this organization yet: no count.
							presence === null
								? null
								: supportCell(
										formatNumber(presence.onEvent, this.locale),
										this.t("admin.analytics.presence.onEvent"),
										pace,
										presence.onEvent > 0,
									),
						]
				: lifecycle === "present"
					? [
							attendanceKnown
								? supportCell(
										formatNumber(door?.arrivalsLast15Min ?? 0, this.locale),
										this.t("admin.events.console.arrivals15Label"),
										null,
										true,
									)
								: null,
							moneyCell ?? (granted ? null : lockCell),
						]
					: [
							// In the last life the money leads, so the door count is the
							// support figure — and where the money is withheld the door count
							// already leads and states itself once.
							moneyCell === null
								? granted
									? null
									: lockCell
								: door === null
									? doorUnanswered
									: attendanceKnown
										? supportCell(
												checkedInFigure,
												this.t("admin.events.console.attendanceFinal"),
												null,
												false,
											)
										: html`<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.noCheckInsYet")}</schmancy-typography>`,
						];
		return html`
			<schmancy-surface
				type="container"
				opacity="translucent"
				class="flex flex-col overflow-hidden rounded-bl-2xl rounded-br-bezel px-4 py-3 md:px-6 md:py-4"
			>
				<schmancy-visually-hidden role="status" aria-live="polite">
					${figuresPending ? this.t("admin.events.console.loadingFigures") : event.name}
				</schmancy-visually-hidden>
				<div class="flex flex-wrap items-end gap-x-8 gap-y-4">
					<!-- The identity is two lines and nothing else: the name, and the
					     one line that places it in time, in space, and — where the band's
					     fraction is the door's — in sales. -->
					<div class="flex min-w-48 max-w-xl flex-1 flex-col gap-1">
						<!-- The puck's inset rides this line alone: the figures below it
						     run the full width of the screen it matters most on. -->
						<schmancy-typography type="headline" token="md" weight="bold" class="pl-14 md:pl-0">${event.name}</schmancy-typography>

						<!-- The countdown rides the schedule line rather than a stat tile
						     of its own: it is a reading OF the date printed beside it. -->
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${schedule}${
								vm && vm.daysToGo > 0
									? ` · ${this.t("admin.events.console.scheduleDaysToGo", { count: vm.daysToGo })}`
									: ""
							}${venueLabel ? ` · ${venueLabel}` : ""}${soldScalar ? ` · ${soldScalar}` : ""}
						</schmancy-typography>
						${when(consoleNotLive.get(), () => html`<schmancy-typography role="status" type="label" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.notLive")}</schmancy-typography>`)}
					</div>

					<div class="flex flex-wrap items-end gap-x-8 gap-y-4">
						${leadCell}
						<div class="flex flex-wrap items-end gap-x-8 gap-y-3">
							${supportCells}
						</div>
					</div>
				</div>
			</schmancy-surface>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"console-headline": ConsoleHeadline;
	}
}
